import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { AuthProvider } from './AuthContext';
import { CurrencyProvider } from './CurrencyContext';
import { ThemeProvider } from './ThemeContext';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';
import { queryPersister, CACHE_VERSION } from './SyncEngine';
import './App.css';

// Long gcTime keeps data resident while the tab is open; the persister
// is what survives reloads via IndexedDB.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24 * 365, // 1y — keep cached data alive so
                                          // returning users see the dashboard
                                          // populated immediately on boot.
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false, // SyncEngine handles focus-based sync
      // Fail fast on transient errors. Default of 3 retries with exponential
      // backoff was making cold-boots against a sleeping backend take 60+s
      // before the loading state resolved. SyncEngine re-fires on focus and
      // network reconnect, so we don't need React Query's retry loop.
      retry: 1,
      retryDelay: 500,
    },
  },
});

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        // 1 year — the user explicitly wants cached data to persist
        // indefinitely so the dashboard renders instantly on every visit.
        maxAge: 1000 * 60 * 60 * 24 * 365,
        buster: CACHE_VERSION,
      }}
    >
      <ThemeProvider>
        <CurrencyProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <Routes>
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<LandingPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthProvider>
          </Router>
        </CurrencyProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}


export default App;
